import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Briefcase, Heart } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useLang } from "../LanguageContext";

export function TeamMemberModal({ member, open, onOpenChange }) {
  const { t, lang } = useLang();
  const [page, setPage] = useState(0);

  if (!member) return null;

  const pages = [
    { key: "roles", label: t("team.tab_roles"), icon: Briefcase, src: member.rolesImage },
    { key: "interests", label: t("team.tab_interests"), icon: Heart, src: member.interestsImage },
  ];
  const current = pages[page];
  const role = lang === "fr" ? member.role_fr : member.role_en;

  const handleOpenChange = (next) => {
    if (!next) setPage(0);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-300 rounded-[1.5rem] p-4 sm:p-6"
        data-testid="team-member-modal"
      >
        <DialogTitle className="sr-only">{`${member.name} — ${current.label}`}</DialogTitle>
        <DialogDescription className="sr-only">{`${member.name} — ${role}`}</DialogDescription>

        <div className="flex items-center justify-between gap-3 mb-4 pr-6">
          <div>
            <p className="font-heading text-lg sm:text-xl text-ink font-medium">{member.name}</p>
            <p className="text-xs sm:text-sm text-ink-muted">{role}</p>
          </div>
          <div className="flex rounded-full bg-cream-200 p-1 shrink-0" role="tablist">
            {pages.map((p, i) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={page === i}
                onClick={() => setPage(i)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  page === i ? "bg-forest text-cream-50" : "text-ink-muted hover:text-forest"
                }`}
                data-testid={`team-modal-tab-${p.key}`}
              >
                <p.icon size={13} aria-hidden />
                <span className="hidden sm:inline">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="relative rounded-[1.1rem] overflow-hidden bg-white shadow-soft">
          <img
            src={current.src}
            alt={`${member.name} — ${current.label}`}
            className="w-full h-auto block"
            data-testid="team-modal-image"
          />

          {pages.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setPage((p) => (p - 1 + pages.length) % pages.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-cream-50/90 text-forest shadow-soft hover:bg-forest hover:text-cream-50 transition-colors"
                aria-label={t("team.prev_page")}
                data-testid="team-modal-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => (p + 1) % pages.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-cream-50/90 text-forest shadow-soft hover:bg-forest hover:text-cream-50 transition-colors"
                aria-label={t("team.next_page")}
                data-testid="team-modal-next"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        <div className="flex justify-center gap-1.5 mt-4">
          {pages.map((p, i) => (
            <span
              key={p.key}
              className={`h-1.5 rounded-full transition-all ${
                page === i ? "w-6 bg-forest" : "w-1.5 bg-cream-300"
              }`}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
